<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      :left-side-class="'col-xl-8'"
      :right-side-class="
        'col-xl-4 align-items-end d-flex pb-2 justify-content-end'
      "
    >
      <template v-slot:customActions>
        <action-button
          v-if="canAdd"
          text="Seleccionar Libros"
          variant="outline-success"
          :responsive="false"
          i="fas fa-check"
          class="book-btn m-1"
          @click="goToRequestSearch"
        />
        <action-button
          v-if="hasBooksPendingOrder"
          class="m-1"
          i="fad fa-share-square"
          variant="outline-success m-1"
          :responsive="false"
          @click="submit"
        >
          Someter Pedido
        </action-button>
      </template>
    </page-header>

    <div class="col-sm-12 col-md-8 col-lg-6 col-xl-3 bg-gray-1 py-2">
      <div class="row">
        <div class="col-sm my-1">
          <custom-drop-down
            v-model="selectedYear"
            fieldkey="id"
            fieldname="description"
            :clearable="false"
            :options="collections.academicYears"
            placeholder="Seleccione"
          />
        </div>
        <div class="col-sm-12 my-1 d-flex justify-content-end col-md-4">
          <action-button
            text="Buscar"
            variant="success"
            i="fas fa-search"
            @click="searchOrders"
          />
        </div>
      </div>
    </div>

    <custom-separator
      text="Libros del Estudiante"
      class="my-4 custom-separator-line"
    />

    <CustomResultNotFound
      v-if="showNotResults"
      class="mt-4 row pt-3"
      text="No se ha encontrado resultado de pedidos de libro para este periodo."
    />

    <div v-if="!showNotResults" class="search-result">
      <OrderBookResultRow
        v-for="(order, idx) in collections.studentBookOrders"
        :key="idx"
        ref="book"
        :value="order"
        :resources="collections.messages"
        :has-books-pending-order="hasBooksPendingOrder"
        @afterRemoved="searchOrders"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import alertMixin from '@/mixin/alert.mixin';
import store from '@/store/index';
import OrderBookResultRow from './components/OrderBookResultRow';
import resourcesService from '@/services/resources.js';
import requestReasonOptions from '@/utils/constants/book/requestReasonOptions';
import {
  getAcademicYearsForBooksOrder,
  getStudentBooksOrders,
  documentBooksOrders,
  submit,
} from '@/modules/books-requests/api';
import {
  studentBookStatus,
  studentBookStatusString,
} from '@/utils/constants/book/studentBookStatus';

const BOOK_REQUEST_SEARCH_ROUTE_NAME = 'bookRequestSearch';

export default {
  name: 'OrderBook',
  mixins: [alertMixin],
  components: {
    PageHeader,
    ActionButton,
    CustomDropDown,
    CustomSeparator,
    CustomResultNotFound,
    OrderBookResultRow,
  },
  data() {
    return {
      canAdd: true,
      selectedYear: null,
      filters: {
        searchText: null,
      },
      collections: {
        studentBookOrders: [],
        academicYears: [],
        messages: [],
      },
    };
  },
  computed: {
    showNotResults() {
      return this.collections.studentBookOrders.length == 0;
    },
    filtersHeader() {
      return [
        {
          name: 'searchText',
          component: 'CustomInput',
          placeholder: 'Escriba Grado, Materia o Título',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          type: 'text',
        },
      ];
    },
    page() {
      return {
        icon: 'fas fa-book-open',
        title: 'Pedido de Libros',
        routes: ['Inicio', 'Libros', 'Pedidos de Libros'],
      };
    },
    hasBooksPendingOrder() {
      return this.collections.studentBookOrders.some(
        (book) => book.status == studentBookStatusString.PendientePedido
      );
    },
  },
  methods: {
    async searchOrders() {
      let studentSieId = store.getters.SelectedStudent.studentId;
      await getStudentBooksOrders(studentSieId, this.selectedYear.id).then(
        (res) => {
          this.collections.studentBookOrders = res.data;
          this.collections.studentBookOrders.forEach((book) => {
            book.requestReason = requestReasonOptions.find(
              (x) => x.id == book.requestReasonCode
            );
          });
        }
      );
    },
    async loadAcademicYears() {
      await getAcademicYearsForBooksOrder()
        .then((response) => {
          if (response.data.length > 0) {
            response.data.forEach((e) => {
              this.collections.academicYears.push({
                id: e.id,
                description: e.description,
              });
            });
          }
        })
        .catch((errm) => {
          this.ShowToast(errm.message, '', 'error');
        });
    },
    async loadResources() {
      this.collections.messages = await resourcesService.LoadValidationMessages(
        ['R03200049', '180100', '180008', '180009']
      );
    },
    async submit() {
      let isValid =
        this.collections.studentBookOrders.some(
          (x) => x.statusId == studentBookStatus.PendientePedido
        ) && this.pendindBooksValid();

      if (!isValid) {
        this.ShowToast(
          'Información Incompleta:',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      const confirm = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea someter el pedido de libros?',
        'Sí',
        'No'
      );

      if (!confirm) {
        return;
      }

      const pendingBooks = this.collections.studentBookOrders.filter(
        (book) => book.statusId == studentBookStatus.PendientePedido
      );
      pendingBooks.forEach((x) => {
        x.requestReasonCode = x.requested ? x.requestReason?.id : null;
        x.requestReason = null;
      });

      await documentBooksOrders(pendingBooks)
        .then((res) => {
          if (res.status == 200) {
            submit(pendingBooks)
              .then((result) => {
                if (result.status == 200) {
                  this.searchOrders();
                  this.ShowToast(
                    'Proceso exitoso.',
                    'El pedido de libros fue sometido exitosamente.',
                    'success'
                  );
                }
              })
              .catch((err) => {
                if (err && err.response && err.response.data) {
                  this.ShowToast('Error', err.response.data.message, 'error');
                } else {
                  this.ShowToast('Error', err.message, 'error');
                }
              });
          }
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    pendindBooksValid() {
      let pendingBooksRowValidations = [];
      this.$refs.book
        .filter(
          (book) => book.value.statusId == studentBookStatus.PendientePedido
        )
        .forEach((book) => {
          pendingBooksRowValidations.push(book.validateBeforeSubmit());
        });

      return pendingBooksRowValidations.every((x) => x);
    },
    goToRequestSearch() {
      this.$router.push({
        name: BOOK_REQUEST_SEARCH_ROUTE_NAME,
        query: { academicYear: this.selectedYear.description },
      });
    },
    collapseAll() {
      this.collections.studentBookOrders.forEach((book, index) => {
        this.$refs.book[index].close();
      });
    },
  },
  async mounted() {
    await this.loadResources();
    await this.loadAcademicYears();
    if (this.$route.query.academicYear)
      this.selectedYear = this.collections.academicYears.find(
        (x) => x.description == this.$route.query.academicYear
      );
    else this.selectedYear = this.collections.academicYears[0];
    await this.searchOrders();
  },
};
</script>

<style scoped>
i.orange-icon.orange-icon-badge-align.fas.fa-book-open {
  font-size: 16pt;
  padding-right: 6px;
}

i.orange-icon.orange-icon-badge-align.fas.fa-book-open {
  font-size: 23px;
  padding-right: 6px;
}

@media only screen and (max-width: 375px) {
  .book-btn {
    float: right;
  }

  ::v-deep .separator-custom__text {
    font-size: 0.75rem;
    font-weight: normal;
    padding: 2px 15px !important;
  }
}
</style>
